import { useFonts } from '@/composables/fonts';
import fontManagerService from '@/utils/fontManagerService';
import { fetchFontFaceMap, downloadFontFile } from '@/utils/amazonFontResolver';
import type { CanvaFont } from '@/typings/layers/layers';

export interface ResolveAmazonFontsResult {
    resolved: CanvaFont[];
    stillMissing: CanvaFont[];
}

export const useAmazonFontImport = () => {
    const fonts = useFonts();

    /**
     * Downloads the .ttf for each missing font from Amazon's fontsCssUrl and registers it
     * as an account font (Fonts, not a Library). Fonts that aren't in the CSS, or that fail
     * to download/upload, are returned in `stillMissing` so the caller can fall back to the
     * manual font replacement flow.
     */
    const resolveAmazonFonts = async (
        fontsCssUrl: string,
        missingFonts: CanvaFont[]
    ): Promise<ResolveAmazonFontsResult> => {
        const resolved: CanvaFont[] = [];
        const stillMissing: CanvaFont[] = [];

        let fontFaceMap: Map<string, string>;
        try {
            fontFaceMap = await fetchFontFaceMap(fontsCssUrl);
        } catch (error) {
            console.error('Error fetching Amazon fonts CSS:', error);
            return { resolved, stillMissing: missingFonts };
        }

        for (const font of missingFonts) {
            const ttfUrl = fontFaceMap.get(font.family);
            if (!ttfUrl) {
                stillMissing.push(font);
                continue;
            }

            try {
                const file = await downloadFontFile(ttfUrl, `${font.family}.ttf`);
                await fonts.uploadUserFont(file);
                resolved.push(font);
            } catch (error) {
                console.error(`Error resolving Amazon font "${font.family}":`, error);
                stillMissing.push(font);
            }
        }

        if (resolved.length) {
            // Refresh both the FontManagerService singleton cache (used by the legacy Angular
            // create-product controller) and the Vuex store (used by Vue components) so the
            // freshly uploaded fonts are immediately matchable during this same import.
            await fontManagerService.getAllFonts(true);
        }

        return { resolved, stillMissing };
    };

    return { resolveAmazonFonts };
};
